import React, { useState, useEffect, useRef } from "react";
import io from "socket.io-client";
import config from "../config";
import { HintCountdown, WordChain } from "./HintSystem";

function MultiplayerSocket() {
  const [username, setUsername] = useState("");
  const [lobbyId, setLobbyId] = useState(null);
  const [gameStarted, setGameStarted] = useState(false);
  const [wordSet, setWordSet] = useState([]);
  const [myProgress, setMyProgress] = useState(0);
  const [opponentProgress, setOpponentProgress] = useState(0);
  const [guess, setGuess] = useState("");
  const [hintCountdown, setHintCountdown] = useState("");
  const [wordHints, setWordHints] = useState({});
  const [gameOver, setGameOver] = useState(false);
  const [winner, setWinner] = useState(null);
  const [connected, setConnected] = useState(false);
  const [animatingWordIndex, setAnimatingWordIndex] = useState(-1);
  const [usernameSet, setUsernameSet] = useState(false);

  const socketRef = useRef(null);
  const usernameRef = useRef("");

  // Rate limiting state
  const lastGuessTime = useRef(0);
  const guessCount = useRef(0);
  const rateLimitWindow = useRef(0);

  // Keep username ref in sync
  useEffect(() => {
    usernameRef.current = username;
  }, [username]);

  const resetGameState = () => {
    setLobbyId(null);
    setGameStarted(false);
    setWordSet([]);
    setMyProgress(0);
    setOpponentProgress(0);
    setGuess("");
    setHintCountdown("");
    setWordHints({});
    setGameOver(false);
    setWinner(null);
    setAnimatingWordIndex(-1);
  };

  const handleJoinNewGame = () => {
    resetGameState();
    if (socketRef.current && username.trim()) {
      socketRef.current.emit("join_lobby", { username: username.trim() });
    }
  };

  useEffect(() => {
    // Initialize Socket.IO connection with WebSocket prioritization
    socketRef.current = io(config.API_BASE_URL, {
      transports: ["websocket", "polling"], // Prioritize WebSocket
      timeout: 20000,
      forceNew: true,
      upgrade: true,
      rememberUpgrade: true,
    });

    const socket = socketRef.current;

    // Connection events
    socket.on("connect", () => {
      setConnected(true);
    });

    socket.on("disconnect", () => {
      setConnected(false);
    });

    // Game events
    socket.on("lobby_joined", (data) => {
      setLobbyId(data.lobby_id);
    });

    socket.on("game_started", (data) => {
      setGameStarted(true);
      setWordSet(data.word_set);
    });

    socket.on("guess_result", (data) => {
      if (data.correct) {
        setMyProgress(data.new_word_index);
        setAnimatingWordIndex(data.new_word_index); // Trigger animation
        setHintCountdown("");
        setWordHints(data.word_hints || {});

        // Clear animation after it completes
        setTimeout(() => setAnimatingWordIndex(-1), 800);

        if (data.game_complete) {
          setGameOver(true);
          setWinner(usernameRef.current); // If we got this event, we won
        }
      } else {
        // Update word hints and countdown
        setWordHints(data.word_hints || {});
        setHintCountdown(data.hint_countdown || "");
      }
    });

    socket.on("progress_update", (data) => {
      // Update opponent progress only (not our own)
      if (data.username !== usernameRef.current) {
        setOpponentProgress(data.progress);
      }
    });

    socket.on("game_complete", (data) => {
      setGameOver(true);
      setWinner(data.winner);
    });

    socket.on("auto_game_started", (data) => {
      setGameStarted(true);
      setWordSet(data.word_set);
    });

    socket.on("error", (data) => {
      console.error("Socket error:", data);
    });

    // Cleanup on unmount
    return () => {
      socket.disconnect();
    };
  }, []); // Remove username dependency to prevent socket recreation on every keystroke

  const handleJoin = () => {
    if (!username.trim() || !connected) return;

    setUsernameSet(true);
    socketRef.current.emit("join_lobby", { username: username.trim() });
  };

  const handleUsernameKeyPress = (e) => {
    if (e.key === "Enter" && username.trim() && connected) {
      handleJoin();
    }
  };

  const handleGuess = (e) => {
    e.preventDefault();
    if (!guess.trim() || !lobbyId || !username || !connected) return;

    // Rate limiting: max 2 guesses per second
    const now = Date.now();
    const timeSinceLastGuess = now - lastGuessTime.current;

    // Reset counter if more than 1 second has passed
    if (timeSinceLastGuess > 1000) {
      guessCount.current = 0;
      rateLimitWindow.current = now;
    }

    // Check if we're within the rate limit window and have exceeded max guesses
    if (now - rateLimitWindow.current < 1000 && guessCount.current >= 2) {
      setHintCountdown("Too fast! Please wait before guessing again.");
      setTimeout(() => setHintCountdown(""), 1000);
      return;
    }

    // Update rate limiting counters
    lastGuessTime.current = now;
    if (now - rateLimitWindow.current < 1000) {
      guessCount.current++;
    } else {
      guessCount.current = 1;
      rateLimitWindow.current = now;
    }

    socketRef.current.emit("submit_guess", {
      lobby_id: lobbyId,
      username,
      guess: guess.trim(),
      word_index: myProgress,
    });

    setGuess("");
  };

  const handleGuessKeyPress = (e) => {
    if (e.key === "Enter" && guess.trim() && !gameOver && connected) {
      handleGuess(e);
    }
  };

  return (
    <div>
      <h1>Word Dash (Race)</h1>

      {!connected && (
        <div style={{ color: "orange", marginBottom: "10px" }}>
          Connecting to server...
        </div>
      )}

      {!lobbyId && connected && !usernameSet && (
        <div>
          <input
            type="text"
            placeholder="Enter your username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            onKeyUp={handleUsernameKeyPress}
          />
          <button onClick={handleJoin} disabled={!username.trim()}>
            Join Lobby
          </button>
        </div>
      )}

      {!lobbyId && connected && usernameSet && (
        <div>
          <p>Welcome back, {username}!</p>
          <button onClick={handleJoinNewGame}>Join New Game</button>
        </div>
      )}

      {lobbyId && !gameStarted && (
        <div>
          <p className="lobby-loading">Waiting for another player...</p>
        </div>
      )}

      {gameStarted && (
        <div>
          <h2>Game Started!</h2>

          <div className="progress-section">
            <div className="progress-item">
              <h4>
                Your Progress ({myProgress + 1}/{wordSet.length})
              </h4>
              <progress
                value={myProgress + 1}
                max={wordSet.length}
                className={
                  myProgress > opponentProgress
                    ? "progress-leading"
                    : myProgress < opponentProgress
                    ? "progress-trailing"
                    : "progress-tied"
                }
              />
            </div>
            <div className="progress-item">
              <h4>
                Opponent's Progress ({opponentProgress + 1}/{wordSet.length})
              </h4>
              <progress
                value={opponentProgress + 1}
                max={wordSet.length}
                className={
                  opponentProgress > myProgress
                    ? "progress-leading"
                    : opponentProgress < myProgress
                    ? "progress-trailing"
                    : "progress-tied"
                }
              />
            </div>
          </div>

          <WordChain
            guessedWords={wordSet.slice(0, myProgress + 1)}
            remainingWords={wordSet.slice(myProgress + 1)}
            wordHints={wordHints}
            currentProgress={myProgress}
            animatingWordIndex={animatingWordIndex}
            isGameOver={gameOver}
          />

          {myProgress < wordSet.length - 1 && !gameOver && (
            <form onSubmit={handleGuess}>
              <input
                type="text"
                value={guess}
                onChange={(e) => setGuess(e.target.value)}
                onKeyUp={handleGuessKeyPress}
                placeholder="Guess the next word"
                disabled={gameOver || !connected}
              />
              <button type="submit" disabled={gameOver || !connected}>
                Guess
              </button>
            </form>
          )}

          <HintCountdown message={hintCountdown} />

          {gameOver && winner && (
            <div className="game-complete-section">
              {winner === username ? (
                <h2 className="game-over" style={{ color: "#4CAF50" }}>
                  🎉 You Won! 🎉
                </h2>
              ) : (
                <h2 className="game-over">
                  😞 You Lost! Better luck next time!
                </h2>
              )}
              <button onClick={handleJoinNewGame}>Play Again</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiplayerSocket;
